/**
 * Custom props: the key → value pairs any tool binding may carry, typed on **Tools** behind Custom props. They are
 * the enterprise's own settings for that tool: the OS stores them as given, names no vendor in them, and each tool's
 * docs page lists the keys it takes. The OS itself reads only the keys named here.
 */
export type BindingProps = Record<string, string>

/** A key: a letter or digit, then letters, digits, `.`, `_` or `-`, 64 characters at most (`git.repoTemplate`). */
export const BINDING_PROP_KEY = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/

/** Most props one binding carries. */
export const MAX_BINDING_PROPS = 50

/** Longest value one prop holds. */
export const MAX_BINDING_PROP_VALUE = 1000

/**
 * The props a workspace reads off a code host's binding (#1025): where it clones from, and what git sends with the
 * binding's token. A binding with none of them is not a code host to a workspace.
 */
export const GIT_PROPS = {
    /** The host the binding's token is valid for, matched against the clone address's host. */
    host: 'git.host',
    /** The username git sends alongside the token. */
    username: 'git.username',
    /** A clone address with `{repo}` in it: how a run's short `owner/name` becomes one. */
    repoTemplate: 'git.repoTemplate',
    /** The repository the binding's layer works in, for a run whose input names none. */
    repo: 'git.repo',
    /**
     * The repositories this binding's host holds (`owner/name` or `owner/*`, comma separated). When several bindings
     * fill one tool slot, a run takes the one that lists its `repo`, so one enterprise can work on several hosts.
     */
    repos: 'git.repos',
} as const

/** Whether a binding's props give a workspace a host, a clone address or a repository to work with. */
export function hasGitProps(props: BindingProps | null | undefined): boolean {
    return Boolean(props?.[GIT_PROPS.host] || props?.[GIT_PROPS.repoTemplate] || props?.[GIT_PROPS.repo])
}
